import React, { useState, useEffect } from 'react';
import { Mail, Server, ShieldCheck, Send, Save, RefreshCw, AlertCircle, X, Inbox, Power } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next'; 

const EmailSettingsModal = ({ isOpen, onClose, orgId }) => {
    const { t, i18n } = useTranslation(); 
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    const [settings, setSettings] = useState({
        SMTP_HOST: '',
        SMTP_PORT: '',
        SMTP_USER: '',
        SMTP_PASS: '',
        SENDER_EMAIL: '',
        IMAP_HOST: '',      // 🎯 إعدادات الرادار الجديد
        IMAP_PORT: '993',   // 🎯 بورت الـ SSL الافتراضي
        ENABLE_IMAP: 'false' // 🎯 مفتاح تشغيل الرادار
    });
    const [loading, setLoading] = useState(false);
    const [testing, setTesting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const fetchSettings = async () => {
                try {
                    const res = await axios.get(`/api/admin/system-settings?org_id=${orgId}&app_name=MAIL`);
                    const settingsObj = {};
                    res.data.forEach(item => {
                        settingsObj[item.setting_key] = item.setting_value;
                    });
                    setSettings(prev => ({ ...prev, ...settingsObj }));
                } catch (err) {
                    console.error("Failed to load settings");
                    toast.error(t('emailSettings.loadFailed')); 
                }
            };
            fetchSettings();
        }
    }, [isOpen, orgId, t]);

    const handleSave = async () => {
        setLoading(true);
        try {
            await axios.post(`/api/admin/save-system-settings`, {
                org_id: orgId,
                app_name: 'MAIL',
                settings: settings
            });
            toast.success(t('emailSettings.saveSuccess')); 
            onClose(); 
        } catch (err) {
            toast.error(t('emailSettings.saveFailed')); 
        } finally {
            setLoading(false);
        }
    };

    const handleTestConnection = async () => {
        setTesting(true);
        const tid = toast.loading(t('emailSettings.connecting')); 
        try {
            const res = await axios.post(`/api/admin/test-email-settings?org_id=${orgId}`);
            toast.success(res.data.message, { id: tid });
        } catch (err) {
            toast.error(err.response?.data?.detail || t('emailSettings.connFailed'), { id: tid }); 
        } finally {
            setTesting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className={`fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-300 ${isRTL ? 'font-cairo dir-rtl text-right' : 'font-sans text-left'}`}>
            <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
                
                {/* Header */}
                <div className={`flex items-center justify-between p-6 border-b bg-slate-50 shrink-0 `}>
                    <div className={`flex items-center gap-3 `}>
                        <div className="bg-[#1a3353] p-2.5 rounded-lg text-white shadow-lg">
                            <Mail size={22} />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-[#1a3353] italic uppercase leading-none tracking-tighter">{t('emailSettings.title')}</h2>
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">{t('emailSettings.subtitle')}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full text-gray-400 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-8 overflow-y-auto custom-scrollbar flex-1">
                    
                    {/* 🎯 قسم البريد الصادر */}
                    <div className={`flex items-center gap-2 mb-4 text-[#1a3353] border-b pb-2 `}>
                        <Send size={16} className="text-orange-500" />
                        <h3 className="font-black uppercase tracking-widest text-[11px]">{t('emailSettings.outgoingTitle')}</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                        <SettingInput label={t('emailSettings.lblHost')} icon={<Server size={14}/>} placeholder="smtp.gmail.com" value={settings.SMTP_HOST} onChange={(val) => setSettings({...settings, SMTP_HOST: val})} isRTL={isRTL} />
                        <SettingInput label={t('emailSettings.lblPort')} icon={<ShieldCheck size={14}/>} placeholder="587" value={settings.SMTP_PORT} onChange={(val) => setSettings({...settings, SMTP_PORT: val})} isRTL={isRTL} />
                        <SettingInput label={t('emailSettings.lblUser')} icon={<Mail size={14}/>} placeholder="user@domain.com" value={settings.SMTP_USER} onChange={(val) => setSettings({...settings, SMTP_USER: val})} isRTL={isRTL} />
                        <SettingInput label={t('emailSettings.lblPass')} icon={<ShieldCheck size={14}/>} placeholder="••••••••••••" value={settings.SMTP_PASS} onChange={(val) => setSettings({...settings, SMTP_PASS: val})} type="password" isRTL={isRTL} />
                        <div className="md:col-span-2">
                            <SettingInput label={t('emailSettings.lblSender')} icon={<Send size={14}/>} placeholder="noreply@company.com" value={settings.SENDER_EMAIL} onChange={(val) => setSettings({...settings, SENDER_EMAIL: val})} isRTL={isRTL} />
                        </div>
                    </div>

                    {/* 🎯 قسم البريد الوارد (رادار المشتريات) */}
                    <div className={`flex items-center gap-2 mb-4 text-[#1a3353] border-b pb-2 mt-4 `}>
                        <Inbox size={16} className="text-blue-500" />
                        <h3 className="font-black uppercase tracking-widest text-[11px]">{t('emailSettings.incomingTitle')}</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* زر التفعيل */}
                        <div className="md:col-span-2 bg-blue-50/50 p-4 rounded-xl border border-blue-100 flex justify-between items-center">
                            <div>
                                <h4 className="font-black text-blue-900 text-[12px] uppercase">{t('emailSettings.enableImap')}</h4>
                                <p className="text-[10px] text-gray-500 italic mt-1">{t('emailSettings.imapDesc')}</p>
                            </div>
                            <button 
                                onClick={() => setSettings({...settings, ENABLE_IMAP: settings.ENABLE_IMAP === 'true' ? 'false' : 'true'})}
                                className={`w-14 h-7 flex items-center rounded-full p-1 transition-colors ${settings.ENABLE_IMAP === 'true' ? 'bg-green-500' : 'bg-gray-300'}`}
                            >
                                <div className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform ${settings.ENABLE_IMAP === 'true' ? (isRTL ? '-translate-x-7' : 'translate-x-7') : 'translate-x-0'}`}></div>
                            </button>
                        </div>

                        <SettingInput label={t('emailSettings.lblImapHost')} icon={<Server size={14}/>} placeholder="imap.gmail.com" value={settings.IMAP_HOST} onChange={(val) => setSettings({...settings, IMAP_HOST: val})} disabled={settings.ENABLE_IMAP !== 'true'} isRTL={isRTL} />
                        <SettingInput label={t('emailSettings.lblImapPort')} icon={<ShieldCheck size={14}/>} placeholder="993" value={settings.IMAP_PORT} onChange={(val) => setSettings({...settings, IMAP_PORT: val})} disabled={settings.ENABLE_IMAP !== 'true'} isRTL={isRTL} />
                    </div>

                    <div className={`md:col-span-2 p-3 bg-orange-50 rounded-xl flex gap-3 items-start border border-orange-100 mt-6 `}>
                        <AlertCircle size={16} className="text-orange-600 shrink-0 mt-0.5" />
                        <p className={`text-[10px] text-orange-900 font-bold leading-relaxed uppercase italic ${isRTL ? 'text-right' : 'text-left'}`}>
                            {t('emailSettings.gmailNote')}
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className={`p-6 border-t bg-slate-50 flex justify-end gap-3 shrink-0 `}>
                    <button onClick={handleTestConnection} disabled={testing} className={`flex items-center gap-2 px-6 py-2 text-orange-600 bg-white border border-orange-200 rounded-xl text-[10px] font-black hover:bg-orange-50 transition-all disabled:opacity-50 `}>
                        {testing ? <RefreshCw size={14} className="animate-spin" /> : <Power size={14} />}
                        {t('emailSettings.btnTest')}
                    </button>
                    <button onClick={onClose} className="px-6 py-2 text-gray-500 bg-white border border-gray-200 rounded-xl text-[10px] font-black hover:bg-gray-50 transition-all">
                        {t('common.cancel')}
                    </button>
                    <button onClick={handleSave} disabled={loading} className={`flex items-center gap-2 px-10 py-2 bg-[#1a3353] text-white rounded-xl text-[10px] font-black hover:bg-black shadow-xl shadow-blue-900/20 transition-all disabled:opacity-50 active:scale-95 `}>
                        {loading ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                        {t('common.save')}
                    </button>
                </div>
            </div>
        </div>
    );
};

const SettingInput = ({ label, icon, placeholder, value, onChange, type = "text", disabled, isRTL }) => (
    <div className={`flex flex-col gap-1 ${disabled ? 'opacity-50 grayscale' : ''} ${isRTL ? 'text-right' : 'text-left'}`}>
        <label className="text-[10px] font-black text-gray-500 uppercase italic">{label}</label>
        <div className="relative group">
            {/* 🎯 الإدخال هنا دايما LTR عشان الروابط والباسوردات بتكتب إنجليزي دايما */}
            <input 
                type={type}
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                dir="ltr"
                className={`w-full py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-all font-bold text-sm text-blue-900 ${isRTL ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'}`}
                placeholder={placeholder}
            />
            <div className={`absolute top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors ${isRTL ? 'right-3' : 'left-3'}`}>
                {icon}
            </div>
        </div>
    </div>
);

export default EmailSettingsModal;